// ============================================================================
// MobileTabBarCool — bottom tab bar del Layout Cool en móvil.
//
// Estructura: 4 favoritos + "Más". Item activo lleva una franjita azul
// curva arriba (indicador) + texto azul-600. Badges naranja sobre el icono.
//
// "Más" abre el drawer del sidebar (mismo Sidebar/SidebarCool en mobile,
// con isOpen=true).
// ============================================================================
import {
  Home, ShoppingCart, LayoutGrid, BookOpen, MoreHorizontal,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useUser } from '../context/user-context';
import { useI18n } from '../context/regional-context';
import type { Clave } from '../../i18n/idiomas';

interface TabItem {
  id: string;
  /** Clave del diccionario (i18n). */
  label: Clave;
  icon: LucideIcon;
}

const TABS: TabItem[] = [
  { id: 'dashboard', label: 'nav.inicio',  icon: Home },
  { id: 'orders',    label: 'nav.orders',  icon: ShoppingCart },
  { id: 'tables',    label: 'nav.tables',  icon: LayoutGrid },
  { id: 'menu',      label: 'nav.menu',    icon: BookOpen },
];

interface MobileTabBarCoolProps {
  activeView: string;
  onChange: (view: string) => void;
  onMoreTap: () => void;
  badges?: Partial<Record<string, number>>;
}

export function MobileTabBarCool({
  activeView, onChange, onMoreTap, badges = {},
}: MobileTabBarCoolProps) {
  const { canViewModule } = useUser();
  const { t: tr } = useI18n();
  return (
    // `flex lg:hidden` via Tailwind — el `display:flex` lo pone la clase, NO
    // el inline style. Si pusiéramos `display:flex` en `style={}` ganaba sobre
    // `lg:hidden` (inline style tiene mayor especificidad que utilities sin
    // !important) y la barra se veía también en desktop. Bug reportado
    // jun-2026.
    <div
      className="flex lg:hidden"
      style={{
        flexShrink: 0,
        background: 'var(--surface)',
        borderTop: '1.5px solid var(--line)',
        padding: '12px 6px 30px',
        paddingBottom: 'calc(30px + env(safe-area-inset-bottom))',
        gap: 0,
      }}
    >
      {/* Oct-2026: no mostrar pestañas de módulos que el rol no ve (el
          superadmin no tiene Pedidos ni Mesas). "Inicio" queda siempre. */}
      {TABS.filter(t => t.id === 'dashboard' || canViewModule(t.id)).map(t => {
        const Icon = t.icon;
        const active = activeView === t.id;
        const badge = badges[t.id];
        return (
          <button
            key={t.id}
            onClick={() => onChange(t.id)}
            className="press"
            style={{
              flex: 1,
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              gap: 4,
              padding: '4px 0',
              background: 'transparent', border: 0,
              fontFamily: 'inherit',
              fontSize: 10.5, fontWeight: 700,
              color: active ? 'var(--blue-600)' : 'var(--muted-2)',
              cursor: 'pointer',
              position: 'relative',
            }}
          >
            {/* Indicador de activo arriba — barrita azul gradient. */}
            {active && (
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute', top: -9,
                  width: 30, height: 3,
                  borderRadius: '0 0 3px 3px',
                  background: 'var(--blue-grad)',
                }}
              />
            )}
            <span style={{ position: 'relative' }}>
              <Icon className="w-[23px] h-[23px]" strokeWidth={2} />
              {typeof badge === 'number' && badge > 0 && (
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute', top: -4, left: '50%',
                    transform: 'translateX(7px)',
                    background: 'var(--warn)', color: '#fff',
                    fontSize: 9, fontWeight: 800,
                    borderRadius: 999, minWidth: 15, height: 15,
                    padding: '0 3px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >{badge > 9 ? '9+' : badge}</span>
              )}
            </span>
            <span>{tr(t.label)}</span>
          </button>
        );
      })}
      <button
        onClick={onMoreTap}
        className="press"
        aria-label={tr('nav.masOpciones')}
        style={{
          flex: 1,
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          gap: 4,
          padding: '4px 0',
          background: 'transparent', border: 0,
          fontFamily: 'inherit',
          fontSize: 10.5, fontWeight: 700,
          color: 'var(--muted-2)',
          cursor: 'pointer',
        }}
      >
        <MoreHorizontal className="w-[23px] h-[23px]" strokeWidth={2} />
        <span>{tr('nav.mas')}</span>
      </button>
    </div>
  );
}
